import * as _ from 'underscore';
import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import { Messages } from '../../../../codegen/Localize';
import { Groups } from '../../Groups';
import { GrantLicenseModes } from '../../../../ts/ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { SiteRoles } from '../../../../ts/ServerApi/types/ISiteRole';
import { openReactActionDialog } from '../../../../commonInfra/dialogs/ReactActionDialog';
import {
  CreateGroupDialogState,
  CreateGroupDialogBody,
  CreateGroupActionDialogProps,
  CreateGroupDialogProps } from '../../components/CreateGroupDialog';
import { DefaultSiteRole } from '../../../services/resources/SiteRoleOptions';
import { ServerSettingsUnauthenticated } from '../../../../settings/services/ServerSettingsUnauthenticated';
import { ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { L10n } from '../../../../ts/L10n/L10n';
import { UserErrorCode } from '../../../../ts/helpers/ErrorCodes';
import { TabDialogWidth } from '../../../../commonInfra/dialogs/style/TabDialogWidth';
import { getAppStore } from '../../../../app/AppStore';
import { requestSpaceContents } from '../../../../app/actions/spaces/RequestSpaceContentsActions';


const initialBodyProps: CreateGroupDialogProps = {};

const initialDialogState: CreateGroupDialogState = {
  groupNameErrorMessage: undefined,
  groupNameText: '',
  grantLicenseMode: undefined,
  isGroupNameTextValid: true,
  minimumSiteRole: DefaultSiteRole
};

export function isSubmitEnabled(state: CreateGroupDialogState): boolean {
  return !_.isEmpty(state.groupNameText) && state.isGroupNameTextValid && !state.groupNameErrorMessage;
}

export function showCreateGroupDialog(): Promise<IResult> {
  const activeDirectoryEnabled = ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory();
  const title = activeDirectoryEnabled ? Messages.createLocalGroupTitle() : Messages.createGroupTitle();
  const actionDisabled = (props: CreateGroupDialogProps, state: CreateGroupDialogState) => {
    return !isSubmitEnabled(state);
  };

  const dialogInstance = openReactActionDialog<CreateGroupDialogProps, CreateGroupDialogState, IResult>({
    actionName: Messages.createGroupDialogSubmit(),
    action: createGroup,
    actionDisabled,
    bodyComponent: CreateGroupDialogBody,
    dialogWidth: TabDialogWidth.width375px,
    dismissDialogOnActionFailed: false,
    initialBodyProps,
    initialDialogState,
    title,
  });

  return Promise.resolve(dialogInstance.result);
}

async function createGroup(
  props: CreateGroupActionDialogProps,
  dialogState: CreateGroupDialogState,
  updateDialogState: React.Dispatch<MergeOrUpdateStateAction<CreateGroupDialogState>>): Promise<IResult> {

  const activeDirectoryEnabled = ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory();
  const { groupNameText, grantLicenseMode, minimumSiteRole } = dialogState;

  if (!activeDirectoryEnabled && grantLicenseMode === GrantLicenseModes.ON_SYNC) {
    updateDialogState({ grantLicenseMode: undefined, minimumSiteRole: SiteRoles.Unlicensed });
  }

  const result = await Groups.createGroup(groupNameText, grantLicenseMode, grantLicenseMode ? minimumSiteRole : undefined);

  return notify(updateDialogState, result, groupNameText);
}

function notify(
  updateDialogState: React.Dispatch<MergeOrUpdateStateAction<CreateGroupDialogState>>,
  result: IResult,
  groupName: string) {
  if (!!result.errors && result.errors.length > 0) {
      const errorMsg: string = errorMessageFor(result.errors[0].code, groupName);
      if (errorMsg) {
        // update dialog to show error message
        updateDialogState({ groupNameErrorMessage: errorMsg, isGroupNameTextValid: false });
        return Promise.reject();
      } else {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.createGroupErrorMsg() });
        return Promise.resolve(result);
      }
  } else {
    getAppStore().dispatch(requestSpaceContents());
    const successMsg = Messages.createGroupSuccessMsg({ NEW_ITEM_NAME: L10n.elideIfLong(groupName) });
    ToasterService.instance.pop({ type: ToastType.Info, text: successMsg });
    return Promise.resolve(result);
  }
}

function errorMessageFor(errorCode: number, groupName: string) {
  switch (errorCode) {
      case UserErrorCode.UserGroupInvalidName:
          return Messages.errorUserGroupInvalidName();
      case UserErrorCode.UserGroupWithNameAlreadyExists:
          return Messages.errorUserGroupWithNameAlreadyExists();
      case UserErrorCode.UserAllUsersGroupAlreadyExists:
          return Messages.errorUserGroupWithNameAlreadyExists();
      default:
          return undefined;
  }
}
